import { GtagService } from 'src/app/gtag/gtag.service';
import { AuthService } from '../../../auth/services/auth.service';
import { ModalService } from '../../../shared/services/modal.service';
import { MediaService } from '../../services/media.service';
import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  ViewContainerRef,
  ComponentFactoryResolver,
} from '@angular/core';
import { Track } from 'src/app/shared/models/tracks-sfx/track.model';
import { Subscription } from 'rxjs';
import { TracksService } from 'src/app/shared/services/tracks.service';
import { ErrorsService } from 'src/app/shared/services/errors.service';
import { WaveService } from 'src/app/wave/wave.service';
import { Options } from '../../../slider/options';
import { UtilsService } from '../../../shared/services/utils.service';

@Component({
  selector: 'hs-player',
  templateUrl: './player.component.html',
  styleUrls: ['./player.component.scss'],
})
export class PlayerComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('waveform') waveContainer: ElementRef;

  @ViewChild('slider', { read: ViewContainerRef })
  private sliderContainer: ViewContainerRef;

  track: Track;
  wavesurfer: any;

  subscribed = false;
  downloading = false;

  @Output() collapsedChange = new EventEmitter<boolean>();

  minVolume = 0;
  volume = 1;

  collapsed = false;

  volumeOptions: Options = {
    floor: 0,
    ceil: 1,
    step: 0.01,
    translate: (): string => {
      return '';
    }
  };

  private subscriptions: Subscription = new Subscription();

  constructor(
    public mediaService: MediaService,
    public modalService: ModalService,
    public auth: AuthService,
    private trackService: TracksService,
    private errorsService: ErrorsService,
    private waveService: WaveService,
    private gtag: GtagService,
    private vcref: ViewContainerRef,
    private cfr: ComponentFactoryResolver,
    private utils: UtilsService
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(this.auth.subscription.subscribe(response => {
      this.subscribed = response.isSubscribed;
    }));
  }

  ngAfterViewInit() {
    this.subscriptions.add(this.mediaService.onUpdate.subscribe(update => {
      if (this.wavesurfer) {
        this.wavesurfer.backend.explicitDuration = update.duration;
        try {
          this.wavesurfer.seekTo(update.seek);
        } catch (e) {
          this.errorsService.track(e, ['WaveSurfer']);
        }
      }
    }));
    setTimeout( () => {
      this.subscriptions.add(this.mediaService.onTrackChange.subscribe(track => {
        this.waveService.get().then(wavesurfer => {
          this.track = track;
          this.gtag.play(track.id);

          if (!this.wavesurfer) {
            const options = {
              container: this.waveContainer.nativeElement,
              backend: 'MediaElement',
              waveColor: 'white',
              progressColor: '#f93d66',
              barHeight: 1,
              barWidth: 1.1,
              cursorColor: 'none',
              height: '45',
              fillParent: true,
              barGap: '3',
              barRadius: 1,
              normalize: true,
              hideScrollbar: true,
              responsive: 500
            };
            this.wavesurfer = wavesurfer.create(options);

            const element = document.getElementById('waveform');
            element.addEventListener('click', (event) => {
              const rect = element.getBoundingClientRect();
              const x = event.clientX - rect.left;
              const calc = x / element.offsetWidth;
              this.mediaService.seekTo(calc);
            }, false);
          }

          if (!this.track.isSoundEffect) {
            try {
              this.wavesurfer.backend.peaks = this.track.waveform;
              this.wavesurfer.drawer.updateSize();
              this.wavesurfer.drawBuffer();
            } catch (e) {
              this.errorsService.track(e, ['WaveSurfer']);
            }
          }
        });
      }));
    }, 100);

    this.loadSlider();
  }

  private async loadSlider() {
    if (this.utils.isBrowser) {
      this.vcref.clear();
      const { SliderComponent } = await import('../../../slider/slider.component');
      const sliderComponent = this.sliderContainer.createComponent(
        this.cfr.resolveComponentFactory(SliderComponent)
      );
      sliderComponent.instance.options = this.volumeOptions;
      sliderComponent.instance.value = this.volume;
      sliderComponent.instance.valueChange.subscribe((value) => {
        this.volume = value;
      });
      sliderComponent.instance.userChange.subscribe((event) => {
        this.setVolume(event);
      });
    }
  }

  next() {
    this.mediaService.next();
  }

  previous() {
    this.mediaService.previous();
  }

  setVolume(event: any) {
    this.volume = event.value;
    this.mediaService.setVolume(this.volume);
  }

  /**
   * Indicates wether the player should be shown or not.
   * This is used to hide the player when playing a SFX.
   * We only show the player for tracks but we still play SFXs through it.
   */
  hidePlayer() {
    return this.track?.isSoundEffect;
  }

  collapsePlayer() {
    this.collapsed = true;
    this.collapsedChange.emit(true);
  }

  expandPlayer() {
    this.collapsed = false;
    this.collapsedChange.emit(false);
  }


  /**
   * Downloads track if user is subscribed
   */
  download() {
    this.downloading = true;
    this.trackService.download(this.track.id).subscribe(response => {
      window.location.href = response.downloadUrl;
      this.downloading = false;
    });
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
    this.wavesurfer?.destroy();
    this.wavesurfer = null;
  }
}
